import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  iconBg?: string;
  iconColor?: string;
  isActive?: (pathname: string) => boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

interface StudentSidebarProps {
  isLoading?: boolean;
}

const StudentSidebar: React.FC<StudentSidebarProps> = ({ isLoading = false }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const courseId = queryParams.get('courseId');
  const classroomId = queryParams.get('classroomId');
  const dashboardHref = courseId && classroomId
    ? `/dashboard-v3?courseId=${courseId}&classroomId=${classroomId}`
    : '/dashboard-v3';

  const sectionData: NavSection[] = [
    {
      title: 'Learn',
      items: [
        {
          label: 'My Classrooms',
          href: '/my-classrooms',
          icon: (
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M7 1L2 3.5 7 6l5-2.5L7 1z" />
              <path d="M2 7l5 2.5L12 7" />
            </svg>
          ),
          iconBg: 'rgba(13, 191, 138, 0.18)',
          iconColor: '#0DBF8A',
          isActive: (pathname) => pathname === '/my-classrooms',
        },
        {
          label: 'Current Course',
          href: dashboardHref,
          icon: (
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="2" width="9" height="9" rx="1.5" />
              <path d="M2 5h9M5 5v6" />
            </svg>
          ),
          iconBg: 'rgba(123, 111, 224, 0.18)',
          iconColor: '#7B6FE0',
          isActive: (pathname) => pathname === '/dashboard' || pathname === '/student-dashboard-v2' || pathname === '/courses-v2' || pathname === '/voice-assessment',
        },
        {
          label: 'Projects',
          href: '/project-center',
          icon: (
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="2" width="10" height="12" rx="1" />
              <path d="M6 6h4M6 9h4" />
            </svg>
          ),
          iconBg: 'rgba(240, 149, 108, 0.18)',
          iconColor: '#F0956C',
          isActive: (pathname) => pathname === '/project-center',
        },
        {
          label: 'Calendar',
          href: '/calendar',
          icon: (
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="3" width="12" height="10" rx="2" />
              <path d="M2 6h12" />
            </svg>
          ),
          iconBg: 'rgba(84, 163, 232, 0.18)',
          iconColor: '#54A3E8',
          isActive: (pathname) => pathname === '/calendar',
        },
      ],
    },
    {
      title: 'My Work',
      items: [
        {
          label: 'My Progress',
          href: '/my-progress',
          icon: (
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M2 10V6M6 10V3M10 10V5" />
            </svg>
          ),
          iconBg: 'rgba(84, 163, 232, 0.18)',
          iconColor: '#54A3E8',
          isActive: (pathname) => pathname === '/my-progress',
        },
        {
          label: 'Portfolio',
          href: '/portfolio',
          icon: (
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M6.5 1L8 4l3 .5-2.5 2 .5 3L6.5 8 3.5 9.5l.5-3L1.5 4.5 4.5 4z" />
            </svg>
          ),
          iconBg: 'rgba(212, 160, 58, 0.18)',
          iconColor: '#D4A03A',
          isActive: (pathname) => pathname === '/portfolio',
        },
        {
          label: 'Career Lab',
          href: '/renkai',
          icon: (
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="4" y="1.5" width="5" height="7" rx="2.5" />
              <path d="M2 6v1a4.5 4.5 0 009 0V6M6.5 11v1.5" />
            </svg>
          ),
          iconBg: 'rgba(123, 111, 224, 0.18)',
          iconColor: '#7B6FE0',
          isActive: (pathname) => pathname === '/renkai',
        },
      ],
    },
    {
      title: 'Community',
      items: [
        {
          label: 'Peer Forum',
          href: '/peer-forum',
          icon: (
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="5" cy="5" r="2.5" />
              <path d="M1 12.5c0-2.2 1.8-4 4-4s4 1.8 4 4" />
            </svg>
          ),
          iconBg: 'rgba(240, 149, 108, 0.18)',
          iconColor: '#F0956C',
          isActive: (pathname) => pathname === '/peer-forum',
        },
        {
          label: 'Innovation Pod',
          href: '/innovation-pod',
          icon: (
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M7 1.5c3 0 5.5 2 5.5 4.5S10 10.5 7 10.5c-.6 0-1.1-.06-1.6-.17L2 12.5v-3.3C1.4 8.2 1.5 7.1 1.5 6c0-2.5 2.5-4.5 5.5-4.5z" />
            </svg>
          ),
          iconBg: 'rgba(13, 191, 138, 0.18)',
          iconColor: '#0DBF8A',
          isActive: (pathname) => pathname === '/innovation-pod',
        },
      ],
    },
    // {
    //   title: 'Career',
    //   items: [
    //     {
    //       label: 'Opportunities',
    //       href: '/job-opportunities',
    //       icon: (
    //         <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
    //           <rect x="1.5" y="4" width="10" height="7" rx="1" />
    //           <path d="M4 4V2.5a1 1 0 011-1h3a1 1 0 011 1V4M1.5 7h10" />
    //         </svg>
    //       ),
    //       isActive: (pathname) => pathname === '/job-opportunities',
    //     },
    //   ],
    // },
  ];

  const handleNavClick = (href: string) => {
    navigate(href);
  };

  if (isLoading) {
    return (
      <aside className="student-sidebar skeleton">
        <style>{`
          .student-sidebar {
            width: 220px;
            background: var(--brand, #346c6a);
            padding: 18px 14px;
            position: sticky;
            top: 0;
            height: 100vh;
            min-height: 100vh;
            overflow-y: auto;
            flex-shrink: 0;
            font-family: 'Space Grotesk', -apple-system, system-ui, sans-serif;
            align-self: flex-start;
          }
          
          .student-sidebar.skeleton {
            display: flex;
            flex-direction: column;
          }
          
          @media (max-width: 900px) {
            .student-sidebar {
              display: none;
            }
          }
          
          .s-section-title-skel {
            font-size: 10px;
            font-weight: 600;
            letter-spacing: 1px;
            text-transform: uppercase;
            color: rgba(255, 255, 255, 0.3);
            padding: 0 10px;
            margin: 18px 0 8px;
          }
          
          .s-section-title-skel:first-of-type {
            margin-top: 0;
          }
          
          .s-nav-item-skel {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 11px 12px;
            border-radius: 10px;
            margin-bottom: 4px;
          }
          
          .s-nav-icon-skel {
            width: 26px;
            height: 26px;
            border-radius: 7px;
            background: rgba(255, 255, 255, 0.06);
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
        `}</style>

        <div className="s-section-title-skel">Learn</div>
        <div className="s-nav-item-skel">
          <div className="s-nav-icon-skel"><Skeleton className="h-4 w-4 rounded bg-white/20" /></div>
          <Skeleton className="h-3.5 w-28 bg-white/20" />
        </div>
        <div className="s-nav-item-skel">
          <div className="s-nav-icon-skel"><Skeleton className="h-4 w-4 rounded bg-white/20" /></div>
          <Skeleton className="h-3.5 w-28 bg-white/20" />
        </div>
        <div className="s-nav-item-skel">
          <div className="s-nav-icon-skel"><Skeleton className="h-4 w-4 rounded bg-white/20" /></div>
          <Skeleton className="h-3.5 w-20 bg-white/20" />
        </div>

        <div className="s-section-title-skel">My Work</div>
        <div className="s-nav-item-skel">
          <div className="s-nav-icon-skel"><Skeleton className="h-4 w-4 rounded bg-white/20" /></div>
          <Skeleton className="h-3.5 w-24 bg-white/20" />
        </div>
        <div className="s-nav-item-skel">
          <div className="s-nav-icon-skel"><Skeleton className="h-4 w-4 rounded bg-white/20" /></div>
          <Skeleton className="h-3.5 w-20 bg-white/20" />
        </div>
        <div className="s-nav-item-skel">
          <div className="s-nav-icon-skel"><Skeleton className="h-4 w-4 rounded bg-white/20" /></div>
          <Skeleton className="h-3.5 w-28 bg-white/20" />
        </div>

        <div className="s-section-title-skel">Community</div>
        <div className="s-nav-item-skel">
          <div className="s-nav-icon-skel"><Skeleton className="h-4 w-4 rounded bg-white/20" /></div>
          <Skeleton className="h-3.5 w-24 bg-white/20" />
        </div>
        <div className="s-nav-item-skel">
          <div className="s-nav-icon-skel"><Skeleton className="h-4 w-4 rounded bg-white/20" /></div>
          <Skeleton className="h-3.5 w-28 bg-white/20" />
        </div>

        <div className="s-section-title-skel">Career</div>
        <div className="s-nav-item-skel">
          <div className="s-nav-icon-skel"><Skeleton className="h-4 w-4 rounded bg-white/20" /></div>
          <Skeleton className="h-3.5 w-28 bg-white/20" />
        </div>
      </aside>
    );
  }

  return (
    <aside className="student-sidebar">
      <style>{`
        .student-sidebar {
          width: 220px;
          background: var(--brand, #346c6a);
          padding: 18px 14px;
          position: sticky;
          top: 0;
          height: 100vh;
          min-height: 100vh;
          overflow-y: auto;
          flex-shrink: 0;
          font-family: 'Space Grotesk', -apple-system, system-ui, sans-serif;
          align-self: flex-start;
        }
        
        @media (max-width: 900px) {
          .student-sidebar {
            display: none;
          }
        }
        
        .s-section-title {
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 1px;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.3);
          padding: 0 10px;
          margin: 18px 0 8px;
        }
        
        .s-section-title:first-child {
          margin-top: 0;
        }
        
        .s-nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 11px 12px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.7);
          cursor: pointer;
          margin-bottom: 4px;
          border: 1px solid transparent;
          text-decoration: none;
          transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease, transform 0.12s ease;
          width: 100%;
          text-align: left;
          background: transparent;
        }
        
        .s-nav-item:hover {
          background: rgba(255, 255, 255, 0.06);
          color: #fff;
        }

        .s-nav-item:focus-visible {
          outline: 2px solid rgba(255, 255, 255, 0.75);
          outline-offset: 2px;
          color: #fff;
        }
        
        .s-nav-item.active {
          background: rgba(255, 255, 255, 0.12);
          color: #fff;
          border-color: rgba(255, 255, 255, 0.1);
        }
        
        .s-nav-icon {
          width: 26px;
          height: 26px;
          border-radius: 7px;
          background: rgba(255, 255, 255, 0.08);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        
        .s-nav-item.active .s-nav-icon {
          transform: scale(1.08);
          box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25);
        }
      `}</style>

      {sectionData.map((section) => (
        <div key={section.title}>
          <div className="s-section-title">{section.title}</div>
          {section.items.map((item) => {
            const isActive = item.isActive
              ? item.isActive(location.pathname)
              : location.pathname === item.href;

            return (
              <button
                type="button"
                key={item.href}
                className={`s-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => handleNavClick(item.href)}
                aria-current={isActive ? 'page' : undefined}
              >
                <div className="s-nav-icon" style={item.iconBg ? { background: item.iconBg, color: item.iconColor } : undefined}>{item.icon}</div>
                {item.label}
              </button>
            );
          })}
        </div>
      ))}
    </aside>
  );
};

export default StudentSidebar;
